import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';

import {
  ArticleCommentListApiParams,
  ArticleCommentListApiResponse,
  DeleteArticleCommentApiParams,
  PatchArticleCommentApiParams,
  PostArticleCommentApiParams,
} from '../apis';

import { clientAxios } from '@/shared/api';

import {
  ApiResponse,
  ArticleComment,
  ArticleCommentType,
} from '@/shared/types';

const convertCommentList = (list: ArticleComment[]) => {
  return list.map((comment) => ({
    rootId: comment.root_comment_id,
    depth: comment.depth,
    id: comment.id,
    comment: comment.comment,
    imgUrl: comment.image_url,
    username: comment.username,
    dateCreated: comment.date_created,
    dateModified: comment.date_modified,
    dateDeleted: comment.date_deleted,
    parentUsername: comment.parent_username,
    isAuthor: comment.is_author,
    isModified: comment.is_modified,
    isDeletedByManager: comment.is_deleted_by_manager,
    isMine: comment.is_mine,
  }));
};

export const useCommentList = ({ id, page }: ArticleCommentListApiParams) => {
  return useQuery({
    queryKey: ['articleCommentsApi', id, page],
    queryFn: async () => {
      const { data } = await clientAxios.get<
        ApiResponse<ArticleCommentListApiResponse>
      >('/articleCommentsApi/', { params: { id, page } });

      const { count, next, prev, results } = data.data;
      const maxPage = Math.ceil(count / 10);

      const convertedComments = convertCommentList(results);

      return {
        count,
        maxPage,
        comments: convertedComments,
      };
    },
    enabled:
      Boolean(id) && id !== 'undefined' && id !== 'null' && !isNaN(Number(id)),
  });
};
export const usePostArticleCommentApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      newsId,
      parentId,
      comment,
      page,
    }: PostArticleCommentApiParams) => {
      const data = await clientAxios.post<ApiResponse<ArticleCommentType>>(
        '/postArticleCommentApi/',
        {
          newsId,
          parentId,
          comment,
        }
      );

      return data.data.data;
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['articleCommentsApi', variables.newsId, variables.page],
      });
      await queryClient.invalidateQueries({
        queryKey: ['articleActionStatusApi', Number(variables.newsId)],
      });
    },
  });
};

export const usePatchArticleCommentApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      newsId,
      commentId,
      parentId,
      comment,
      page,
    }: PatchArticleCommentApiParams) =>
      clientAxios.patch<ApiResponse<unknown>>('/patchArticleCommentApi/', {
        newsId,
        parentId,
        commentId,
        comment,
      }),
    onSuccess: async (data, variables) => {
      return await queryClient.invalidateQueries({
        queryKey: ['articleCommentsApi', variables.newsId, variables.page],
      });
    },
  });
};

export const useDeleteArticleCommentApi = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ newsId, commentId, page }: DeleteArticleCommentApiParams) =>
      clientAxios.delete<ApiResponse<unknown>>('/deleteArticleCommentApi/', {
        data: { newsId, commentId },
      }),
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: ['articleCommentsApi', variables.newsId, variables.page],
      });
      await queryClient.invalidateQueries({
        queryKey: ['articleActionStatusApi', Number(variables.newsId)],
      });
    },
  });
};
